import { ROOT_ID, applyToPoint, invert as invertMatrix, type NodeRecord, type Scene, type VectorPath } from '@flobi/artwork-core';
import { emitHintSignal } from '@flobi/asset-library/hints';
import { anchorCount, simplifyVectorPath } from './simplifyPath';
import { TIDY_ICONS, TIDY_LAYOUTS } from './tidyUp';
import type { IconName } from './chrome/icons';
import type { MenuEntry } from '../ui/menu';
import type { CanvasHandle, ToolId } from './useEditorCanvas';
import { clipboardSubmenu, type ClipboardOptions } from './clipboardMenu';
import type { Rail, RailButton } from './tools/types';

interface Options {
  nodes: readonly NodeRecord[];
  canvas: CanvasHandle;
  /** The active point editor supplies the same commands as its toolbar. */
  pointRail?: Rail | null;
  /** The visible artboard beneath this context click, independent of object selection. */
  artboard?: NodeRecord;
  at: { x: number; y: number };
  canUndo: boolean;
  canRedo: boolean;
  undo(): void;
  redo(): void;
  textEnabled: boolean;
  /** A phone: Layers is offered from the title menu, which can also put it away. */
  phone?: boolean;
  tool(id: ToolId): void;
  properties(): void;
  /** The Info tab on a picture (what it is; how it was made, when generated). */
  info?(): void;
  layers(): void;
  assets(): void;
  image(): void;
  /** Opens Export set to the layers chosen, alone. */
  exportSelection(): void;
  /** Save only this explicit artboard and its contents. */
  saveTemplate(id: string): void;
  /** Sends this explicit artboard to Flow as a Layer node (cross-app pages); offered only when given. */
  sendToFlow?(id: string): void;
  /** Flow's App Directory logo on that row. */
  flowLogo?: string | null;
  /** Asks for another picture to put in these image layers, in place. */
  replaceImage(ids: readonly string[]): void;
  /** Render the selected layers into generation references. */
  addReference?(ids: readonly string[]): void;
  /** Pins the layers chosen to the agent's next message and opens its panel. */
  addToChat?(nodes: readonly NodeRecord[]): void;
  /** A layer by id, for naming what lies under the pointer. */
  node(id: string): NodeRecord | undefined;
  /** Cut, Copy, Paste, Paste here and Copy as, one level down in Edit (W5-AR3). */
  clipboard?: Omit<ClipboardOptions, 'chosen' | 'editable' | 'here'>;
}

const LAYER_ICONS: Partial<Record<string, IconName>> = {
  group: 'group', text: 'text', image: 'pictures', rect: 'square', ellipse: 'circle',
  polygon: 'polygon', star: 'star', line: 'line', path: 'pen', cloner: 'cloner', blend: 'blend',
};

/** Build from the current selection, including type-specific actions and real availability. */
export function canvasMenu(o: Options): MenuEntry[] {
  if (o.pointRail?.pointEditing?.total) return pointMenu(o.pointRail, o);
  const { nodes, canvas } = o;
  const one = nodes.length === 1 ? nodes[0] : undefined;
  const ids = nodes.map(n => n.id);
  const editable = nodes.some(n => !n.locked);
  const add: MenuEntry = { kind: 'submenu', id: 'add', label: 'Add', icon: 'plus', items: [
    ...(['rect', 'ellipse', 'polygon', 'star'] as const).map((kind, i) => ({
      id: kind, label: ['Rectangle', 'Ellipse', 'Polygon', 'Star'][i]!,
      icon: (['square', 'circle', 'polygon', 'star'] as const)[i],
      onSelect: () => canvas.insertShape(kind, o.at),
    })),
    ...(o.textEnabled ? [{ id: 'text', label: 'Draw text', icon: 'text' as const, shortcut: 'T', onSelect: () => o.tool('text') }] : []),
    { id: 'image', label: 'Place image…', icon: 'pictures', onSelect: o.image },
    { id: 'artboard', label: 'Draw artboard', icon: 'artboard', shortcut: '⇧O', onSelect: () => o.tool('artboard') },
    { kind: 'separator' },
    { id: 'assets', label: 'Browse assets…', icon: 'files', onSelect: o.assets },
  ] };
  // Everything under the pointer, however it is nested: the one way to a layer buried
  // under others without opening group after group. Only offered when there is a choice.
  const under = canvas.layersAt(o.at).flatMap((id) => {
    const node = o.node(id);
    return node ? [node] : [];
  });
  const pick: MenuEntry[] = under.length > 1 ? [{ kind: 'submenu', id: 'pick', label: 'Select layer', icon: 'cursor', items: under.map((node) => ({
    id: `pick-${node.id}`,
    label: node.name || node.type[0]!.toUpperCase() + node.type.slice(1),
    icon: LAYER_ICONS[node.type],
    onSelect: () => canvas.select([node.id]),
  })) }] : [];
  const items: MenuEntry[] = [{ kind: 'header', label: one?.name || (one ? one.type[0]!.toUpperCase() + one.type.slice(1) : nodes.length ? `${nodes.length} layers selected` : 'Canvas') }];
  items.push(...pick);
  if (o.clipboard) items.push(clipboardSubmenu({ ...o.clipboard, chosen: nodes.length > 0, editable, here: o.at }));
  // A locked layer cannot be picked, so a right-click on one chose nothing and the only way
  // to unlock it was the Layers panel (QA 2026-10-05, ART-SEL-24). It is offered here.
  if (!nodes.length) {
    const locked = (canvas.lockedAt?.(o.at) ?? []).flatMap((id) => {
      const node = o.node(id);
      return node ? [node] : [];
    });
    const called = (node: NodeRecord) => node.name || node.type[0]!.toUpperCase() + node.type.slice(1);
    if (locked.length === 1) items.push({ id: 'unlock-under', label: `Unlock “${called(locked[0]!)}”`, icon: 'unlock', onSelect: () => canvas.updateAll([locked[0]!.id], { locked: false }) });
    else if (locked.length > 1) {
      items.push({ kind: 'submenu', id: 'unlock-under', label: 'Unlock', icon: 'unlock', items: locked.map((node) => ({
        id: `unlock-${node.id}`,
        label: called(node),
        icon: LAYER_ICONS[node.type],
        onSelect: () => canvas.updateAll([node.id], { locked: false }),
      })) });
    }
    if (locked.length > 0) items.push({ kind: 'separator' });
  }
  if (nodes.length) {
    items.push({ id: 'properties', label: 'Properties', icon: 'sidebar-right', onSelect: o.properties });
    if (o.info) items.push({ id: 'info', label: 'Info', icon: 'info', onSelect: o.info });
    // Every image chosen takes the one picture picked: the same frame, another photograph.
    if (nodes.every(n => n.type === 'image')) {
      const pictures = nodes.filter(n => !n.locked).map(n => n.id);
      if (pictures.length) items.push({ id: 'replace-image', label: nodes.length > 1 ? 'Replace images…' : 'Replace image…', icon: 'pictures', onSelect: () => o.replaceImage(pictures) });
    }
    if (o.addReference) items.push({ id: 'add-reference', label: 'Add as reference', icon: 'pictures', onSelect: () => o.addReference!(ids) });
    if (o.addToChat) items.push({ id: 'add-to-chat', label: 'Add to chat', onSelect: () => o.addToChat!(nodes) });
    // A locked selection can't be edited: what it can't do is left out, not shown greyed
    // (omit, never grey). Lock/Unlock, Hide and the view rows stay.
    if (one?.type === 'text' && editable) items.push({ id: 'edit-text', label: 'Edit text', icon: 'text', onSelect: () => canvas.editText(one.id) });
    // Type as paths, and anything as one picture: both take the chosen layers' place.
    if (editable && nodes.some(n => n.type === 'text' && !n.locked)) items.push({ id: 'outline-text', label: 'Convert to outlines', icon: 'format-vector', onSelect: () => void canvas.outlineText() });
    if (editable && nodes.some(n => !n.locked) && !nodes.some(n => ['artboard', 'slice', 'effector'].includes(n.type))) items.push({ id: 'rasterize', label: 'Rasterize', icon: 'pictures', onSelect: () => void canvas.rasterize() });
    if (one?.type === 'cloner' && editable) items.push({ id: 'edit-cloner', label: 'Edit repeat', icon: 'cloner', onSelect: () => { o.tool('cloner'); o.properties(); } });
    // Worked out on the press, not while the menu opens: a traced outline can have thousands of points.
    if (nodes.some(simplifiable)) items.push({ id: 'simplify-points', label: 'Simplify points', icon: 'magic', onSelect: () => canvas.updateEach(simplifyWrites(nodes).map(({ id, path }) => ({ id, props: { path } }))) });
    if (editable) items.push({ id: 'duplicate', label: 'Duplicate', icon: 'copy', shortcut: '⌘D', onSelect: () => { canvas.duplicate(); emitHintSignal('layer.duplicated', { viaButton: true }); } });
    if (nodes.filter(n => !n.locked).length >= 2) items.push({ id: 'group', label: 'Group selection', icon: 'group', shortcut: '⌘G', onSelect: () => { canvas.group(); emitHintSignal('layers.grouped', { viaButton: true }); } });
    if (editable && nodes.some(n => ['group', 'boolean', 'blend'].includes(n.type))) items.push({ id: 'ungroup', label: 'Ungroup', icon: 'ungroup', shortcut: '⇧⌘G', onSelect: () => canvas.ungroup() });
    if (editable) items.push({ kind: 'submenu', id: 'arrange', label: 'Arrange', icon: 'layers', items: [
      { id: 'front', label: 'Bring to front', shortcut: '⇧⌘]', onSelect: () => { canvas.arrange('front'); emitHintSignal('layers.arranged', { viaMenu: true }); } },
      { id: 'forward', label: 'Bring forward', shortcut: '⌘]', onSelect: () => { canvas.arrange('forward'); emitHintSignal('layers.arranged', { viaMenu: true }); } },
      { id: 'backward', label: 'Send backward', shortcut: '⌘[', onSelect: () => { canvas.arrange('backward'); emitHintSignal('layers.arranged', { viaMenu: true }); } },
      { id: 'back', label: 'Send to back', shortcut: '⇧⌘[', onSelect: () => { canvas.arrange('back'); emitHintSignal('layers.arranged', { viaMenu: true }); } },
      { kind: 'separator' },
      { id: 'flip-h', label: 'Flip horizontal', icon: 'flip-h', onSelect: () => canvas.flip('x') },
      { id: 'flip-v', label: 'Flip vertical', icon: 'flip-v', onSelect: () => canvas.flip('y') },
    ] });
    // One layer aligns to its artboard, so a lone layer off every artboard has nothing to align to.
    if (editable && (nodes.length > 1 || canvas.selectionPlace()?.board)) items.push({ kind: 'submenu', id: 'align', label: 'Align', icon: 'align-left', items: [
      ...(['left', 'centre', 'right', 'top', 'middle', 'bottom'] as const).map((how, i) => ({
        id: how, label: ['Left', 'Horizontal centre', 'Right', 'Top', 'Vertical centre', 'Bottom'][i]!,
        icon: (['align-left', 'align-center', 'align-right', 'align-top', 'align-middle', 'align-bottom'] as const)[i],
        onSelect: () => { canvas.align(how); emitHintSignal('layers.aligned', { count: nodes.length, spread: false }); },
      })),
      ...(nodes.length >= 3 ? [
        { kind: 'separator' as const },
        { id: 'spread-x', label: 'Distribute horizontally', onSelect: () => { canvas.align('spread-x'); emitHintSignal('layers.distributed'); } },
        { id: 'spread-y', label: 'Distribute vertically', onSelect: () => { canvas.align('spread-y'); emitHintSignal('layers.distributed'); } },
      ] : []),
    ] });
    // Several layers laid out again, evenly spaced: a grid, a masonry wall, a column, a row.
    if (nodes.filter(n => !n.locked).length >= 2) items.push({ kind: 'submenu', id: 'tidy-up', label: 'Tidy up', icon: 'magic', items:
      TIDY_LAYOUTS.map(({ how, label }) => ({ id: `tidy-${how}`, label, icon: TIDY_ICONS[how], onSelect: () => canvas.tidyUp(how) })) });
    // What is chosen as a file of its own: a logo, a group, one drawing out of a page.
    if (canvas.exportLayers()) items.push({ id: 'export-selection', label: 'Export selection…', icon: 'export-download', onSelect: o.exportSelection });
    const templateBoard = one?.type === 'artboard' ? one : o.artboard;
    if (templateBoard?.type === 'artboard') items.push({ id: 'save-template', label: one?.type === 'artboard' ? 'Save as template…' : 'Save artboard as template…', icon: 'templates', note: one?.type === 'artboard' ? undefined : templateBoard.name || 'Artboard', onSelect: () => o.saveTemplate(templateBoard.id) });
    // This artboard alone, as layers; the title menu and a Home row's Open with send the whole file.
    if (templateBoard?.type === 'artboard' && o.sendToFlow) items.push({ kind: 'submenu', id: 'open-with', label: 'Open artboard with', icon: 'export', items: [
      { id: 'send-to-flow', label: 'Flow', ...(o.flowLogo !== undefined ? { logo: o.flowLogo } : {}), note: one?.type === 'artboard' ? undefined : templateBoard.name || 'Artboard', onSelect: () => o.sendToFlow!(templateBoard.id) },
    ] });
    const locked = nodes.every(n => n.locked);
    items.push({ kind: 'separator' },
      { id: 'lock', label: locked ? 'Unlock' : 'Lock', icon: locked ? 'unlock' : 'lock', onSelect: () => canvas.updateAll(ids, { locked: !locked }) },
      { id: 'hide', label: 'Hide', icon: 'eye-off', onSelect: () => canvas.updateAll(ids, { visible: false }) },
      ...(editable ? [{ id: 'delete', label: 'Delete', icon: 'trash' as const, shortcut: '⌫', danger: true, onSelect: () => canvas.deleteSelection() }] : []),
      { kind: 'separator' });
  }
  items.push(add,
    { kind: 'submenu', id: 'view', label: 'View', icon: 'eye', items: [
      { id: 'fit', label: 'Zoom to fit', shortcut: '0', onSelect: () => { canvas.fit(); emitHintSignal('view.zoomed', { viaMenu: true }); } },
      { id: 'actual', label: 'Actual size', shortcut: '1', onSelect: () => { canvas.actualSize(); emitHintSignal('view.zoomed', { viaMenu: true }); } },
      { kind: 'separator' },
      // On a phone Layers is in the title menu, which can also put it away.
      ...(o.phone ? [] : [{ id: 'layers', label: 'Layers', icon: 'layers' as const, onSelect: o.layers }]),
      { id: 'properties', label: 'Properties', icon: 'sidebar-right', onSelect: o.properties },
    ] },
    ...(!nodes.length ? [{ id: 'select-all', label: 'Select all', icon: 'select-area' as const, shortcut: '⌘A', onSelect: () => canvas.selectAll() }] : []),
    { kind: 'separator' },
    { id: 'undo', label: 'Undo', icon: 'undo', shortcut: '⌘Z', disabled: !o.canUndo, onSelect: o.undo },
    { id: 'redo', label: 'Redo', icon: 'redo', shortcut: '⇧⌘Z', disabled: !o.canRedo, onSelect: o.redo });
  return items;
}

/**
 * The chosen paths that Simplify points would actually thin, with what each becomes:
 * fewer anchors, the same shape, points on top of each other merged. A path with nothing
 * to take out is left out, so the item is only offered when it would do something.
 */
export function simplifyWrites(nodes: readonly NodeRecord[]): { id: string; path: VectorPath }[] {
  return nodes.flatMap((node) => {
    if (!simplifiable(node) || node.type !== 'path') return [];
    const path = simplifyVectorPath(node.path);
    return anchorCount(path) < anchorCount(node.path) ? [{ id: node.id, path }] : [];
  });
}

/** An unlocked path with points enough to spare some. */
function simplifiable(node: NodeRecord): boolean {
  return node.type === 'path' && !node.locked && !!node.path?.subpaths?.length && anchorCount(node.path) >= 4;
}

/** Anchor commands never fall through to layer deletion, duplication or arrangement. */
function pointMenu(rail: Rail, o: Options): MenuEntry[] {
  const count = rail.pointEditing!.selected;
  const edits = rail.middle?.kind === 'buttons' ? rail.middle.items : [];
  const helpers = rail.bottom?.kind === 'buttons' ? rail.bottom.items : [];
  const modes = rail.top?.kind === 'buttons' ? rail.top.items : [];
  const command = (item: RailButton): Extract<MenuEntry, { onSelect(): void }> => ({
    id: item.id, label: item.label ?? item.title.split(' — ')[0]!, icon: item.icon,
    disabled: item.disabled, checked: item.pressed, onSelect: () => item.run({ more: false }),
  });
  const selectItems = ['all-points', 'deselect-points', 'grow', 'shrink', 'invert', 'mirror'].flatMap(id => {
    const item = helpers.find(item => item.id === id);
    return item ? [command(item)] : [];
  });
  const done = modes.find(item => item.id === 'object');
  return [
    { kind: 'header', label: count ? `${count} ${count === 1 ? 'point' : 'points'} selected` : 'Edit points' },
    ...edits.filter(item => item.id === 'smooth' || item.id === 'corner').map(command),
    { kind: 'separator' },
    ...edits.filter(item => item.id === 'add-point' || item.id === 'close-path' || item.id === 'simplify-points').map(item => ({
      ...command(item), ...(item.id === 'add-point' ? { label: 'Add point…', note: 'Click the path to insert a point' } : {}),
    })),
    ...edits.filter(item => item.id === 'remove-points').map(item => ({
      ...command(item), label: count === 1 ? 'Delete point' : count > 1 ? `Delete ${count} points` : 'Delete points',
      shortcut: '⌫', danger: true,
    })),
    { kind: 'separator' },
    { kind: 'submenu', id: 'select-points', label: 'Select points', icon: 'select-point', items: selectItems },
    ...(done ? [{ ...command(done), label: 'Finish point editing', icon: 'check' as const, checked: undefined, shortcut: 'Esc' }] : []),
    { kind: 'separator' },
    { id: 'undo', label: 'Undo', icon: 'undo', shortcut: '⌘Z', disabled: !o.canUndo, onSelect: o.undo },
    { id: 'redo', label: 'Redo', icon: 'redo', shortcut: '⇧⌘Z', disabled: !o.canRedo, onSelect: o.redo },
  ];
}

/** Preserve multi-selection or an explicitly selected child when right-clicking it. */
export function contextSelection(selection: readonly string[], hit: string | null, selectedChildAt: (ancestor: string, child: string) => boolean, insideSelection = false, artboard: string | null = null): string[] {
  if (!hit) return insideSelection && selection.length ? [...selection] : artboard ? [artboard] : [];
  const target = selection.find(id => id === hit || selectedChildAt(hit, id)) ?? hit;
  return selection.includes(target) ? [...selection] : [target];
}

/** Blank/transparent artboard areas still belong to the board. Object hit testing deliberately skips them. */
export function contextArtboardAt(scene: Scene, world: { x: number; y: number }): string | null {
  for (const id of [...scene.childrenOf(ROOT_ID)].reverse()) {
    const board = scene.get(id);
    if (board?.type !== 'artboard' || scene.hiddenHere(id)) continue;
    const inverse = invertMatrix(scene.worldTransform(id));
    if (!inverse) continue;
    const local = applyToPoint(inverse, world.x, world.y);
    if (local.x >= 0 && local.y >= 0 && local.x <= board.width && local.y <= board.height) return id;
  }
  return null;
}
